import {useCallback, useEffect, useState} from "react";
import {fetchPendingOrders, PENDING_ORDERS_CHANGED_EVENT} from "../infrastructure/pendingOrderApi";
import {getOrElse, initial, isSuccess, pending} from "../../common/domain/RemoteData.model";
import {useComercialContext} from "../../comercial/ComercialContext";

/**
 * Hook con los pedidos pendientes del cliente (o del cliente suplantado en
 * modo comercial), ya agrupados por producto por el backend.
 *
 * El reparto entre "ya hay stock" (restocked) y "en espera" (waiting) sale del
 * flag isRestocked que calcula el backend: no se recalcula aquí.
 */
export const usePendingOrders = () => {
    const [pendingData, setPendingData] = useState(initial([]));
    const {actingAsCliente} = useComercialContext();

    const refresh = useCallback(async () => {
        setPendingData(pending());
        const result = await fetchPendingOrders(actingAsCliente?.numCliente ?? null);
        setPendingData(result);
    }, [actingAsCliente?.numCliente]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Cualquier mutación de pendientes (añadir desde el split, eliminar, servir) dispara
    // PENDING_ORDERS_CHANGED_EVENT: todas las instancias del hook se refrescan al momento.
    // En silencio (sin pasar por pending()) para que el badge y las listas no parpadeen.
    useEffect(() => {
        const onChanged = async () => {
            const result = await fetchPendingOrders(actingAsCliente?.numCliente ?? null);
            setPendingData((current) => (isSuccess(result) ? result : current));
        };
        window.addEventListener(PENDING_ORDERS_CHANGED_EVENT, onChanged);
        return () => window.removeEventListener(PENDING_ORDERS_CHANGED_EVENT, onChanged);
    }, [actingAsCliente?.numCliente]);

    const removeLocally = useCallback((productCode) => {
        setPendingData((current) => {
            if (!isSuccess(current)) {
                return current;
            }
            return {...current, value: current.value.filter((it) => it.product.code !== productCode)};
        });
    }, []);

    const allPending = getOrElse(pendingData, []) ?? [];
    const restocked = allPending.filter((it) => it.isRestocked);
    const waiting = allPending.filter((it) => !it.isRestocked);

    return {pendingData, allPending, restocked, waiting, refresh, removeLocally};
};
